"use client";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { usePathname } from "next/navigation";
import {
  X,
  ChevronRight,
  ShoppingBag,
  Utensils,
  Search,
  Bus,
  Store,
  ReceiptText,
  PlaneTakeoff,
  Newspaper,
  Plane,
  MapPin,
  Map as MapIcon,
} from "lucide-react";
import Link from "next/link";
import { fetchVuelos } from "@/lib/api/index";
import { Vuelos } from "@/lib/api/vuelos/types";
import { formatHora } from "@/lib/utils";
import { fetchAeropuerto } from "@/lib/api/datosGenerales/fetchAeropuerto";
import { Aeropuerto } from "@/lib/api/datosGenerales/types/aeropuerto";
import { useLocale } from "next-intl";
import { type Locale } from "@/i18n/config";

export default function Hero() {
  const t = useTranslations("hero");
  const pathname = usePathname();
  // const locale = pathname.split("/")[1];
  const locale = useLocale() as Locale;
  const isCorporate = pathname.startsWith(`/${locale}/corporativo`);
  const [data, setData] = useState<Aeropuerto | null>(null);

  const buttonItems = isCorporate
    ? [
        {
          label: t("concursos"),
          icon: <Store className="w-6 h-6 mx-auto mb-2" />,
          href: `/corporativo/concursosComerciales`,
        },
        {
          label: t("licitaciones"),
          icon: <ReceiptText className="w-6 h-6 mx-auto mb-2" />,
          href: "/corporativo/transparencia",
        },
        {
          label: t("noticias"),
          icon: <Newspaper className="w-6 h-6 mx-auto mb-2" />,
          href: "/corporativo/noticias",
        },
        {
          label: t("sitioAeropuerto"),
          icon: <Plane className="w-6 h-6 mx-auto mb-2" />,
          href: "/",
        },
      ]
    : [
        {
          label: t("tiendas"),
          icon: <ShoppingBag className="w-6 h-6 mx-auto mb-2" />,
          href: "/tiendas",
        },
        {
          label: t("restaurantes"),
          icon: <Utensils className="w-6 h-6 mx-auto mb-2" />,
          href: "/restaurantes",
        },
        {
          label: t("shuttleBus"),
          icon: <Bus className="w-6 h-6 mx-auto mb-2" />,
          href: "/transporte/shuttlebus",
        },
        {
          label: t("mapa"),
          icon: <MapIcon className="w-6 h-6 mx-auto mb-2" />,
          href: "/map",
        },
      ];

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<"arrivals" | "departures">(
    "departures",
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [resultados, setResultados] = useState<Vuelos[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const handleSearch = useCallback(
    async (searchTerm: string, type: "arrivals" | "departures") => {
      setIsLoading(true);
      try {
        const data = await fetchVuelos(type, "hoy");
        const trimmed = searchTerm.trim().toLowerCase();
        const filtrados =
          trimmed.length === 0
            ? data.slice(0, 5)
            : data
                .filter(
                  (v) =>
                    v.routingDescEs?.toLowerCase().includes(trimmed) ||
                    v.routing.toLowerCase().includes(trimmed) ||
                    `${v.vuelo || ""}${v.vueloNum || ""}`
                      ?.toLowerCase()
                      .includes(trimmed) ||
                    v.vueloNum?.toLowerCase().includes(trimmed) ||
                    v.codeshare?.some((c) =>
                      c.code?.toLowerCase().includes(trimmed),
                    ),
                )
                .slice(0, 5);
        setResultados(filtrados);
      } catch (err) {
        console.error("Error al buscar vuelos:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      handleSearch(search, selected);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [search, selected, handleSearch]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    async function loadData() {
      try {
        const aeropuerto = await fetchAeropuerto(locale);
        setData(aeropuerto);
      } catch (error) {
        console.error("Error al cargar los datos de Qué hacer:", error);
      }
    }
    loadData();
  }, [locale]);

  const images = useMemo(() => {
    if (isCorporate) {
      return [
        "https://galeria.tocumenaero.com/gallery/api/Generales/321A0372.jpg",
        "https://galeria.tocumenaero.com/gallery/api/Movimiento%20de%20pasajeros/321A7353.jpg",
        "https://galeria.tocumenaero.com/gallery/api/Terminal%201%20y%202%20fachada%20d%C3%ADa%20y%20noche/321A5606.jpg",
      ];
    }

    // Si no hay data aún, mostramos una imagen temporal
    if (!data) {
      return ["/img2.jpg"];
    }

    return [
      data.banner1 || "/img2.jpg",
      "https://galeria.tocumenaero.com/gallery/api/Terminal%201%20y%202%20fachada%20d%C3%ADa%20y%20noche/321A5563.jpg",
      data.banner3,
    ];
  }, [data, isCorporate]);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <section data-hero className="relative w-full h-screen">
      <div className="absolute inset-0 overflow-hidden">
        {images.map((src, i) => (
          <Image
            key={i}
            src={src}
            alt={`Banner ${i + 1}`}
            fill
            sizes="100vw"
            priority={i === index}
            className={`absolute object-cover transition-opacity duration-1000 ease-in-out transform-gpu ${
              i === index
                ? "opacity-100 scale-105 z-0 animate-zoomSlow"
                : "opacity-0 scale-110"
            }`}
          />
        ))}
      </div>

      {/* Contenido centrado vertical u horizontal según el tipo */}
      <div
        // pb-24 hasta lg: por debajo de ese ancho los accesos rápidos ocupan
        // todo el ancho y el último choca con el botón flotante del chat, que
        // llega hasta ~88px del borde inferior. Desde lg los accesos son la
        // columna derecha del hero y ya no lo alcanzan.
        className={`absolute inset-0 px-4 pt-4 pb-24 lg:pb-4 flex justify-end ${
          isCorporate ? "flex-col items-center mb-4" : "flex-col items-center"
        }`}
      >
        <div
          className={`flex flex-col ${
            isCorporate
              ? "items-center"
              : "lg:flex-row gap-6 w-full max-w-4xl items-start"
          }`}
        >
          {!isCorporate && (
            <div
              ref={wrapperRef}
              className="relative bg-black/20 backdrop-blur-xs hover:backdrop-blur-lg hover:bg-white/90 px-6 py-6 rounded-2xl w-full lg:w-2/3 transition-all duration-300 ease-in-out group shadow-2xl border border-white/10 hover:border-white/30"
              style={{ zIndex: 50 }}
            >
              <div className="flex flex-col md:flex-row gap-2 text-2xl md:text-3xl font-semibold text-white mb-4 group-hover:text-[var(--fondoGeneral-secciones)] transition-all duration-300 ease-in-out">
                <h1>{t("vuelo")}</h1>
              </div>

              {/* Selector de tipo de vuelo */}
              <div className="flex gap-2 mb-4">
                {[
                  {
                    type: "departures",
                    label: t("dropdown.salidas"),
                    icon: "/Salidas.svg",
                  },
                  {
                    type: "arrivals",
                    label: t("dropdown.llegadas"),
                    icon: "/Llegadas.svg",
                  },
                ].map(({ type, label, icon }) => (
                  <button
                    key={type}
                    onClick={() => {
                      setSelected(type as "arrivals" | "departures");
                      setShowDropdown(true);
                    }}
                    className={`flex items-center gap-2 text-sm px-4 py-2 rounded-lg font-medium transition-all duration-200 cursor-pointer ${
                      selected === type
                        ? "bg-[var(--fondoGeneral-secciones)] text-white shadow-lg"
                        : "bg-white/20 text-white group-hover:bg-gray-100 group-hover:text-gray-700 hover:bg-white/30 group-hover:hover:bg-gray-200"
                    }`}
                  >
                    <Image
                      src={icon}
                      alt={label}
                      width={16}
                      height={16}
                      className={`w-4 h-4 transition-all duration-200 ${
                        selected === type
                          ? "contrast-[99] invert"
                          : "contrast-[99] invert group-hover:contrast-100 group-hover:invert-0"
                      }`}
                    />
                    {label}
                  </button>
                ))}
              </div>

              <div className="relative">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={t("placeholder")}
                  className="w-full placeholder:italic border-2 border-transparent rounded-xl py-3 pl-12 text-lg pr-12 bg-white/95 text-[var(--fondoGeneral-secciones)] focus:outline-none focus:ring-2 focus:ring-[var(--fondoGeneral-secciones)] focus:border-[var(--fondoGeneral-secciones)] transition-all duration-200 shadow-inner"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--fondoGeneral-secciones)]/60">
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-[var(--fondoGeneral-secciones)]/30 border-t-[var(--fondoGeneral-secciones)] rounded-full animate-spin" />
                  ) : (
                    <Search size={20} />
                  )}
                </div>
                {search && (
                  <button
                    onClick={() => {
                      setSearch("");
                      setShowDropdown(false);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-[var(--fondoGeneral-secciones)] transition-colors p-1 rounded-full hover:bg-gray-100"
                  >
                    <X size={20} />
                  </button>
                )}

                {/* Dropdown de resultados mejorado */}
                {showDropdown && (
                  <div
                    className="absolute top-full left-0 w-full mt-2 bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100 animate-in fade-in slide-in-from-top-2 duration-200"
                    style={{ zIndex: 100 }}
                  >
                    {/* Header del dropdown */}
                    <div className="px-4 py-3 bg-gradient-to-r from-[var(--fondoGeneral-secciones)] to-[var(--fondoGeneral-secciones)]/80 text-white">
                      <p className="text-sm font-medium flex items-center gap-2">
                        {selected === "departures" ? (
                          <>
                            <PlaneTakeoff className="w-4 h-4" />
                            {t("dropdown.salidas")}
                          </>
                        ) : (
                          <>
                            <Plane className="w-4 h-4 rotate-90" />
                            {t("dropdown.llegadas")}
                          </>
                        )}
                        <span className="ml-auto text-white/70 text-xs">
                          {resultados.length}{" "}
                          {resultados.length === 1 ? "resultado" : "resultados"}
                        </span>
                      </p>
                    </div>

                    {/* Lista de resultados */}
                    <div className="max-h-80 overflow-y-auto">
                      {resultados.length === 0 ? (
                        <div className="px-6 py-8 text-center">
                          <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                            <Search className="w-8 h-8 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium">
                            No hay resultados
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            Intenta con otro término de búsqueda
                          </p>
                        </div>
                      ) : (
                        resultados.map((vuelo, index) => (
                          <div
                            key={vuelo.id}
                            className={`flex items-center gap-4 px-4 py-4 hover:bg-[var(--fondoGeneral-secciones)]/5 cursor-pointer transition-colors group/item ${
                              index !== resultados.length - 1
                                ? "border-b border-gray-100"
                                : ""
                            }`}
                            onClick={() => router.push(`/vuelos/${vuelo.id}`)}
                          >
                            {/* Hora */}
                            <div className="flex-shrink-0 w-16 text-center">
                              <div className="bg-[var(--fondoGeneral-secciones)]/10 rounded-lg px-2 py-1">
                                <span className="text-lg font-bold text-[var(--fondoGeneral-secciones)]">
                                  {formatHora(vuelo.horaEstimada || "N/A")}
                                </span>
                              </div>
                            </div>

                            {/* Info del vuelo */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-[var(--fondoGeneral-secciones)]/60 flex-shrink-0" />
                                <span className="font-semibold text-[var(--fondoGeneral-secciones)] truncate">
                                  {vuelo.routingDescEs}
                                </span>
                                <span className="text-xs text-gray-400 font-medium">
                                  ({vuelo.routing})
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Plane className="w-3 h-3 text-gray-400" />
                                <span className="text-sm text-gray-500">
                                  {vuelo.vuelo}
                                  {vuelo.vueloNum}
                                  {vuelo.codeshare?.length
                                    ? `, ${vuelo.codeshare.map((c) => c.code).join(", ")}`
                                    : ""}
                                </span>
                                {vuelo.terminal && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                    {t("terminal")} {vuelo.terminal}
                                  </span>
                                )}
                                {vuelo.puerta && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                    {t("puerta")} {vuelo.puerta}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Flecha */}
                            <ChevronRight className="w-5 h-5 text-gray-300 group-hover/item:text-[var(--fondoGeneral-secciones)] group-hover/item:translate-x-1 transition-all" />
                          </div>
                        ))
                      )}
                    </div>

                    {/* Footer con botón */}
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                      <button
                        onClick={() => {
                          const tipoURL =
                            selected === "arrivals" ? "llegada" : "salida";
                          router.push(
                            `/vuelos?tipo=${tipoURL}&search=${search}`,
                          );
                        }}
                        className="w-full bg-[var(--fondoGeneral-secciones)] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 hover:bg-[var(--fondoGeneral-secciones)]/90 hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                      >
                        {t("dropdown.verMas")}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Botones de acceso rápido */}
          <div
            className={`grid gap-3 w-full h-full ${
              isCorporate
                ? "mt-8 grid-cols-2 md:grid-cols-4"
                : "lg:w-1/3 grid-cols-2"
            }`}
          >
            {buttonItems.map(({ label, icon, href }, i) => (
              <Link
                key={i}
                href={href}
                className="group/btn bg-black/20 backdrop-blur-xs text-white py-5 px-4 rounded-2xl font-semibold text-center hover:backdrop-blur-lg hover:bg-white/90 hover:text-[var(--fondoGeneral-secciones)] transition-all duration-300 flex flex-col items-center justify-center gap-1 border border-white/10 hover:border-white/30 hover:shadow-xl hover:-translate-y-1"
              >
                <div className="">{icon}</div>
                <span className="text-sm">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
